/**
 * The `setup` namespace, in English — the B0 workspace setup wizard (KLQ-20260819-002).
 *
 * Its own namespace rather than more keys in `app`, for the reason `lib/i18n/catalog.ts`
 * gives: namespaces are split by screen family so a language pack can ship one family at a
 * time. B0 is a screen family a firm sees exactly once, and it is the one screen where a
 * Slovak architect is *choosing* the language everything after it is written in — so it is
 * the last place to make a translator finish the whole product first.
 *
 * COUNTRY COPY. Country names and the `…meta` lines are catalog entries and not constants,
 * because both translate: "Czech Republic" is "Tschechien" in German, and `DD.MM.YYYY` is
 * `TT.MM.JJJJ`. The standards named inside a meta line (STN, ČSN, HOAI, DIN) are proper nouns
 * and stay as they are in every language — they name a standards body, not a concept.
 *
 * NORM COPY. There is no longer a `norm.*` label per standard, and that is `KLQ-20260827-001`
 * rather than an oversight: the wizard renders `GET /api/v1/norms`, and a norm's name arrives
 * from the catalogue in the language of the law it names — *"Slovensko, stavebný zákon a
 * vyhlášky"* reads that way on a German screen for the same reason "Slovenčina" does. What is
 * left here is everything the product says *about* a norm, which does translate.
 *
 * Company-language chips are deliberately **not** here: an endonym ("Slovenčina") is written
 * the same way whatever language the surrounding page is in, which is the entire point of an
 * endonym. They live in `features/setup/presets.ts` beside the locale codes they set.
 *
 * LENGTH: `scripts/check-expansion.mjs` pseudo-expands every message here by 1.3×. Keys
 * ending in `.title` are measured against the 3-line heading slot; everything else against
 * the wrapping-prose budget. A message that trips it is shortened here, never given more
 * room there.
 */

export const setupEn = {
  // TWIN: `brand.wordmark` in `./entrance.ts` is the same word for the entrance screens.
  // Deliberately duplicated rather than shared, so a language pack can ship one namespace
  // without the other (ADR-0010's forced-conversation pattern, retro rule 12) — if the
  // wordmark ever changes, it changes in both files.
  'brand.wordmark': 'KALQA',
  'topbar.context': 'Company setup',

  // --- The page (`KLQ-20261023-009`: one screen, one forced field) ----------
  'page.title': 'Name your studio',
  'page.subtitle':
    'That is the only thing we need. Everything below already has an answer from your browser; change it now or later in Studio settings.',

  'field.name.label': 'Company name',
  'field.name.placeholder': 'Novák Statika',
  'field.name.hint': 'What colleagues and clients will see on drawings and in invitations.',
  'field.slug.label': 'Address in Kalqa',
  'field.slug.placeholder': 'novak-statika',
  // Changeable until the first shared link uses it (`KLQ-20261023-009`, owner 2026-10-28):
  // `PATCH /workspace` accepts a slug while `slug_changeable` is true. The permanence warning
  // moved from "always" to "once you have shared", which is when it starts being true.
  // TWIN: `field.slug.hintChangeable` / `field.slug.hint` in `./settings.ts` say the same rule
  // after the choice; if it changes, it changes in both files (retro rule 12).
  'field.slug.hint':
    'Optional. Lower-case letters, digits and single hyphens. You can change it in Studio settings until the first shared link uses it.',

  // --- Summary lines -------------------------------------------------------
  // Each line is an answer already given, with a link that opens the control beneath it.
  'summary.address': 'Address: {address}',
  'summary.locale': 'Language and country: {language} · {country}',
  'summary.noCountry': 'no country',
  'summary.norms': 'Standards: {norms}',
  'summary.change': 'change',

  'error.nameRequired': 'Enter the name of your company.',
  'error.nameLong': 'Use {max} characters or fewer.',
  'error.slugShort': 'Use at least {min} characters.',
  'error.slugLong': 'Use {max} characters or fewer.',
  'error.slugPattern': 'Use lower-case letters, digits and single hyphens.',
  'error.avvRequired':
    'We cannot finish setting up your company without the data processing agreement.',
  'avv.prefix': 'On behalf of the company, I enter into the',
  'avv.document': 'data processing agreement',
  'avv.suffix': 'under Art. 28 GDPR.',
  'error.slugTaken': 'That address is taken. Choose another one.',

  // --- Behind „change“: country, language, norms ---
  'group.centralEurope': 'Central Europe (bundled preset)',
  'group.centralEurope.note':
    'These four share enough common ground that Kalqa bundles them into one preset. Switch the locale per project if you take on a job in another country.',
  'group.otherRegions': 'Other regions',
  'group.norms': 'Standards you work to',
  'group.norms.note':
    'Ticked from the country you picked. Tick as many as your office works to; you can add more later in Studio settings. At least one is needed.',
  'group.norms.default': 'New projects start on',
  'norms.loading': 'Loading standards…',
  // A norm that is in the catalogue with no phase ladder seeded. Drawn and disabled rather
  // than hidden: the country exists, and "not yet" is a truer answer than silence.
  'norms.unavailable': 'Not yet',
  'norms.unavailable.why': 'We have not published the phases for this standard yet.',
  'norms.unreadable':
    'We could not load the list of standards. The one for your country is already selected, and you can change it later in Studio settings.',
  'group.language': 'Company language',
  'group.language.note':
    'Affects document defaults, generated mail and invitations. Each person picks their own interface language in their profile.',

  'country.common': 'Common',
  'country.selected': 'Selected',

  'country.SK.name': 'Slovakia',
  'country.SK.meta': 'STN · A0–A4 · DD.MM.YYYY',
  'country.CZ.name': 'Czech Republic',
  'country.CZ.meta': 'ČSN · A0–A4 · DD.MM.YYYY',
  'country.AT.name': 'Austria',
  // HOAI and not ÖNORM: Austria ticks the DIN/HOAI ladder, because ÖNORM has no phases
  // published yet. A meta line that named ÖNORM would promise what the tick does not send.
  // TWIN: the `AT` row in `features/setup/presets.ts`.
  'country.AT.meta': 'HOAI · A0–A4 · DD.MM.YYYY',
  'country.DE.name': 'Germany',
  'country.DE.meta': 'DIN · A0–A4 · DD.MM.YYYY',
  'country.HU.name': 'Hungary',
  'country.HU.meta': 'MSZ · A0–A4',
  'country.PL.name': 'Poland',
  'country.PL.meta': 'PN · A0–A4',
  'country.CH.name': 'Switzerland',
  'country.CH.meta': 'SIA · A0–A4',
  'country.GB.name': 'United Kingdom',
  'country.GB.meta': 'BS · A0–A4',
  'country.US.name': 'United States',
  'country.US.meta': 'ANSI · Arch B–E',
  'country.XX.name': 'Other / EN-ISO baseline',
  'country.XX.meta': 'Generic European norm',
  'country.XX.code': '—',

  // --- Footer actions -------------------------------------------------------
  'action.finish': 'Finish setup',
  'action.signOut': 'Sign out',

  // --- Whole-submit failures ------------------------------------------------
  'error.forbidden': 'You cannot set this company up. Ask whoever administers it.',
  'error.rateLimited': 'Too many attempts. Wait a minute and try again.',
  'error.validation': 'Check the details above, then try again.',
  'error.unexpected': 'Something went wrong on our side. Try again in a moment.',
} as const;

export type SetupMessageKey = keyof typeof setupEn;
